import { Outlet, Navigate } from "react-router-dom";
import { LoginRedirect, ReturnTargetConsumer } from "../LoginRedirect";
import { Sidebar } from "./Sidebar";
import { Breadcrumbs } from "./Breadcrumbs";
import { SuspendedBanner } from "./SuspendedBanner";
import { ClaimsChatWidget } from "../chat/ClaimsChatWidget";
import { SeqReportWidget } from "../report/SeqReportWidget";
import { IdleTimeoutProvider } from "../IdleTimeoutProvider";
import { SidebarProvider, useSidebar } from "./SidebarContext";
import { SafeModeProvider } from "../../contexts/SafeModeContext";
import { useAuth } from "../../hooks/useAuth";
import { useBranding } from "../../hooks/useBranding";
import { useSubscription } from "../../hooks/useSubscription";

function AppLayoutInner() {
  const { user, profile, loading, hasOrgAccess } = useAuth();
  const { branding } = useBranding();
  const { isSuspended, subscription } = useSubscription();
  const { isCollapsed } = useSidebar();

  // Use branding for loading/guard screens
  const appName = branding.company_name || branding.app_name;
  const logoUrl = branding.logo_url || "/claims-app-logo.svg";

  if (loading) {
    return (
      <div className="app-layout">
        {/* Skeleton sidebar */}
        <aside className="sidebar-skeleton">
          <div className="sidebar-skeleton-header">
            <div className="skeleton" style={{ width: 36, height: 36, borderRadius: 8 }} />
            <div style={{ flex: 1 }}>
              <div className="skeleton" style={{ width: "70%", height: 12, marginBottom: 6 }} />
              <div className="skeleton" style={{ width: "50%", height: 10 }} />
            </div>
          </div>
          <div className="sidebar-skeleton-nav">
            {[1, 2, 3].map((i) => (
              <div key={i} className="sidebar-skeleton-item">
                <div className="skeleton" style={{ width: 20, height: 20, borderRadius: 6 }} />
                <div className="skeleton" style={{ width: `${60 + i * 10}%`, height: 12 }} />
              </div>
            ))}
          </div>
        </aside>

        {/* Skeleton content */}
        <main className="app-main">
          <div className="app-content">
            {/* Breadcrumb skeleton */}
            <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
              <div className="skeleton" style={{ width: 80, height: 14 }} />
              <div className="skeleton" style={{ width: 12, height: 14 }} />
              <div className="skeleton" style={{ width: 100, height: 14 }} />
            </div>

            {/* Metric cards skeleton */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 20, marginBottom: 32 }}>
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="card" style={{ padding: 24 }}>
                  <div className="skeleton" style={{ width: "60%", height: 12, marginBottom: 12 }} />
                  <div className="skeleton" style={{ width: "40%", height: 28, marginBottom: 8 }} />
                  <div className="skeleton" style={{ width: "80%", height: 10 }} />
                </div>
              ))}
            </div>

            {/* Table skeleton */}
            <div className="card" style={{ padding: 24 }}>
              <div className="skeleton" style={{ width: 180, height: 18, marginBottom: 20 }} />
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} style={{ display: "flex", gap: 16, marginBottom: 16 }}>
                  <div className="skeleton" style={{ width: "25%", height: 14 }} />
                  <div className="skeleton" style={{ width: "35%", height: 14 }} />
                  <div className="skeleton" style={{ width: "20%", height: 14 }} />
                  <div className="skeleton" style={{ width: "20%", height: 14 }} />
                </div>
              ))}
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Not logged in
  if (!user) {
    return <LoginRedirect />;
  }

  // Logged in but setup not complete
  if (profile && !profile.setup_complete) {
    return <Navigate to="/complete-setup" replace />;
  }

  // Logged in but no org access (replaces legacy profile.claims_access check)
  if (profile && !hasOrgAccess) {
    return (
      <div className="loading-overlay">
        <img
          src={logoUrl}
          alt={appName}
          style={{ width: 120, height: 120, marginBottom: "var(--spacing-lg)" }}
        />
        <h2 style={{ marginBottom: "var(--spacing-md)" }}>Access Restricted</h2>
        <p
          style={{
            color: "var(--text-secondary)",
            textAlign: "center",
            maxWidth: 400,
          }}
        >
          You don't have access to the Claims application. Please contact an
          administrator to request access.
        </p>
      </div>
    );
  }

  return (
    <IdleTimeoutProvider>
      {profile && <ReturnTargetConsumer />}
      <div className="app-layout">
        <Sidebar />
        <main className={`app-main ${isCollapsed ? "sidebar-collapsed" : ""}`}>
          {isSuspended && subscription?.status && (
            <SuspendedBanner status={subscription.status} />
          )}
          <div className="app-content">
            <Breadcrumbs />
            <div className="app-page-content">
              <Outlet />
            </div>
          </div>
        </main>
        <ClaimsChatWidget />
        <SeqReportWidget />
      </div>
    </IdleTimeoutProvider>
  );
}

export function AppLayout() {
  return (
    <SafeModeProvider>
      <SidebarProvider>
        <AppLayoutInner />
      </SidebarProvider>
    </SafeModeProvider>
  );
}
